// Entrance resolution for the three dedicated doors into the ONE application:
//   team.tfsi-us.com   → workforce (TEAM)
//   client.tfsi-us.com → customers (ROUT Client Portal)
//   app.tfsi-us.com    → internal Total-E administrative hub
//
// Hostname is the production signal. For preview (a single shared domain with
// no subdomain DNS yet) an explicit `?e=team|client|app` override is persisted
// so every door can be tested before the subdomains are activated. Entrance is
// a routing/branding hint ONLY — it never grants access. All authorization is
// enforced server-side from account capabilities, membership and scope.
import { defaultRouteFor } from '@/lib/workspaces';

const KEY = 'te_entrance';
const VALID = ['team', 'client', 'app'];

const fromHost = () => {
  try {
    const h = (window.location.hostname || '').toLowerCase();
    if (h.startsWith('team.')) return 'team';
    if (h.startsWith('client.')) return 'client';
    if (h.startsWith('app.')) return 'app';
  } catch { /* ignore */ }
  return null;
};

const fromOverride = () => {
  try { const v = localStorage.getItem(KEY); return VALID.includes(v) ? v : null; }
  catch { return null; }
};

// Capture a `?e=` preview override once, persist it, and strip it from the URL.
// `?e=reset` clears the override (returns to the bare-domain legacy entrance).
export const captureEntranceParam = () => {
  try {
    const p = new URLSearchParams(window.location.search);
    const raw = (p.get('e') || '').toLowerCase();
    if (!raw) return;
    if (VALID.includes(raw)) localStorage.setItem(KEY, raw);
    else if (raw === 'reset') localStorage.removeItem(KEY);
    else return;
    p.delete('e');
    const qs = p.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
  } catch { /* ignore */ }
};

// Production hostname wins; the preview override is the fallback.
export const getEntrance = () => fromHost() || fromOverride();

export const entranceLoginPath = (e = getEntrance()) =>
  e === 'team' ? '/team' : e === 'client' ? '/rout' : '/login';

export const entranceLandingPath = (user, e = getEntrance()) =>
  e === 'team' ? '/team-hub' : e === 'client' ? '/client' : defaultRouteFor(user);

// Where a sign-out / expired-session returns: the current door's own login,
// so users are never dropped onto a different entrance or a public chooser.
export const postLogoutPath = () => entranceLoginPath();
